import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import "./Loan.css";
import { withStyles } from "@mui/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ConfrirmDeleteAccountDialog from "../ConfirmDeleteAccountDialog.js";
import EditAccountDialog from "../EditAccountDialog";
import { getColumns } from "../TableData";
import {
  Grid,
  TableCell,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  IconButton,
  Link,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AccountCard from "../../AccountCard";

const StyledTableCell = withStyles({
  root: {
    color: "white",
  },
})(TableCell);

const columns = getColumns();

export default function Loan({
  currentRow,
  setCurrentRow,
  accountType,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const rows = useSelector((state) => state.loans.items);

  //initialize vars
  let [openConfirmDeleteAccount, setOpenConfirmDeleteAccount] = useState(false);
  let [openEditAccountDialog, setOpenEditAccountDialog] = useState(false);
  let [filteredRows, setFilteredRows] = useState([]);
  let height = window.innerHeight * 0.89;

  function filterRowsByType(typeToFilter) {
    filteredRows = rows.filter((row) => {
      return row.type === typeToFilter;
    });

    return filteredRows;
  }

  // Calculate totals
  const total = filteredRows.reduce((total, row) => total + row.remainingBalance, 0);
  const formattedTotal = total.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

  useEffect(() => {
    setFilteredRows(filterRowsByType(accountType));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const handleEdit = (row) => {
    setCurrentRow(row);
    setOpenEditAccountDialog(true);
  };

  const handleEditClose = () => {
    setCurrentRow({});
    setOpenEditAccountDialog(false);
  };

  const handleDelete = (row) => {
    setCurrentRow(row);
    setOpenConfirmDeleteAccount(true);
  };

  return (
    <>
      <ConfrirmDeleteAccountDialog
        open={openConfirmDeleteAccount}
        setOpen={setOpenConfirmDeleteAccount}
        row={currentRow}
        setCurrentRow={setCurrentRow}
      />
      <EditAccountDialog
        open={openEditAccountDialog}
        handleClose={handleEditClose}
        row={currentRow}
      />

      <Grid container style={{ height: "100%" }}>
        <Grid item xs={12}>
          {isMobile ? (
            <>
              {filteredRows.map((row) => (
                <AccountCard
                  key={row.code}
                  row={row}
                  columns={columns}
                  linkAction={
                    row.url && (
                      <Link
                        href={row.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        color="primary"
                        underline="always"
                        sx={{ mr: 1 }}
                      >
                        Go To
                      </Link>
                    )
                  }
                  actions={
                    <>
                      <IconButton size="small" onClick={() => handleEdit(row)}>
                        <EditIcon style={{ fill: "white" }} fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(row)}>
                        <DeleteIcon style={{ fill: "white" }} fontSize="small" />
                      </IconButton>
                    </>
                  }
                />
              ))}
              <AccountCard row={{ remainingBalance: total }} columns={columns} isTotal />
            </>
          ) : (
          <Paper
            sx={{
              width: "100%",
              overflow: "hidden",
              px: { xs: 1, sm: 5 },
              backgroundColor: "#1b262c",
            }}
          >
            <TableContainer
              sx={{
                // Below "sm" the table only scrolls horizontally, not
                // vertically too - a box panning in both directions fights
                // itself on a touch screen (a horizontal swipe can get read
                // as the vertical scroll instead). The Table's own minWidth
                // below is what drives the horizontal scroll predictably;
                // percentage column widths against unbreakable multi-word
                // headers were making real column proportions erratic.
                maxHeight: { xs: "none", sm: height },
                "& .MuiTableRow-root:hover": {
                  backgroundColor: "#333",
                },
              }}
            >
              <Table stickyHeader aria-label="sticky table" size="small" sx={{ minWidth: 750 }}>
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <StyledTableCell
                        key={column.id}
                        align={column.align}
                        style={{
                          width: column.width,
                          backgroundColor: "#0f4c75",
                        }}
                      >
                        {column.label}
                      </StyledTableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredRows.map((row) => {
                    return (
                      <TableRow
                        hover={true}
                        role="checkbox"
                        tabIndex={-1}
                        key={row.code}
                      >
                        {columns.map((column) => {
                          const value = row[column.id];
                          return (
                            <StyledTableCell
                              key={column.id}
                              align={column.align}
                            >
                              {column.id === "icon" ? (
                                <>
                                  <IconButton onClick={() => handleEdit(row)}>
                                    <EditIcon style={{ fill: "white" }} />
                                  </IconButton>
                                  <IconButton onClick={() => handleDelete(row)}>
                                    <DeleteIcon style={{ fill: "white" }} />
                                  </IconButton>
                                </>
                              ) : (
                                column.id === "url" ? (
                                  value && (
                                    <Link
                                      href={value}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      color="primary"
                                      underline="always"
                                    >
                                      Go To
                                    </Link>
                                  )
                                ) : column.id === "monthlyPayment" || column.id === "remainingBalance" ? (
                                  value.toLocaleString("en-US", {
                                    style: "currency",
                                    currency: "USD",
                                    minimumFractionDigits: 2,
                                  })
                                ) : (
                                  value
                                )
                              )}
                            </StyledTableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                  <TableRow>
                    <StyledTableCell />
                    <StyledTableCell />
                    <StyledTableCell />
                    <StyledTableCell />
                    <StyledTableCell />
                    <StyledTableCell style = {{textAlign: "center"}}>Total:</StyledTableCell>
                    <StyledTableCell style = {{textAlign: "center"}}>{formattedTotal}</StyledTableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          )}
        </Grid>
      </Grid>
    </>
  );
}
